import pluralize from 'pluralize'

import { FvButton, FvLinkButton } from '@fv/client-components'
import { showApiError } from '@fv/client-core'
import type { ListShipment } from '@fv/client-types'
import { BATCH_PRINT_LIMIT } from '@fv/models'

import InfoBox from '@/components/InfoBox'
import { List, ListItem } from '@/components/List'
import { ShipperSlider } from '@/components/ShipperSlider'
import { routes } from '@/routes'

import { ShipperOnly } from '../auth/HasRole'
import { useAccountSettings } from '../settings/account-settings/hooks'
import { isDocumentTypeEnabled } from './batchPrintEligibility'
import type { BatchPrintDocumentType } from './types'
import { useBatchPrintDocument } from './useBatchPrintDocument'
import { useBatchPrintEligibility } from './useBatchPrintEligibility'

type CloseFn = (deselect?: boolean) => void
type BaseProps = {
  loads: ListShipment[]
  onClose: CloseFn
}
type SliderProps = BaseProps & {
  isOpen: boolean
}

export const BatchPrintSlider = ({ loads, onClose, isOpen }: SliderProps) => {
  return (
    <ShipperSlider width="32rem" isOpen={isOpen} onClose={onClose}>
      <SliderContents loads={loads} onClose={onClose} />
    </ShipperSlider>
  )
}

const SliderContents = ({ loads, onClose }: BaseProps) => {
  const settings = useAccountSettings()
  const summary = useBatchPrintEligibility(loads)
  const batchPrint = useBatchPrintDocument()
  const paperworkSettingsPath = routes.settings('account-settings')
  const overLimit = loads.length > BATCH_PRINT_LIMIT

  const handlePrint = async (type: BatchPrintDocumentType) => {
    try {
      await batchPrint.mutateAsync({ loads, shipping: settings.shipping, type })
      onClose(true)
    } catch (error) {
      showApiError('Error generating paperwork', error)
    }
  }
  return (
    <div className="flex flex-col h-full -mx-6">
      <div className="flex-1 overflow-auto pb-4 px-6">
        {overLimit ? (
          <InfoBox>
            Select at most {BATCH_PRINT_LIMIT} shipments to print paperwork.
          </InfoBox>
        ) : (
          <>
            <p className="mb-3">
              Choose a document type to print for {loads.length} selected{' '}
              {pluralize('shipment', loads.length)}.
            </p>

            {summary.accountGateBlocked && summary.accountGateMessage && (
              <InfoBox className="mb-4">
                {summary.accountGateMessage}{' '}
                <ShipperOnly permissions={['manage-settings']}>
                  <FvLinkButton theme="callout" to={paperworkSettingsPath}>
                    Paperwork settings
                  </FvLinkButton>
                </ShipperOnly>
              </InfoBox>
            )}

            <List>
              {summary.documentOptions.map(option => {
                const enabled = isDocumentTypeEnabled(option)
                const eligibleCount = option.eligibleCount

                return (
                  <ListItem key={option.type} custom className="!block py-3">
                    <FvButton
                      theme="default"
                      icon="print"
                      className="w-full"
                      disabled={!enabled || batchPrint.isPending}
                      onClick={() => handlePrint(option.type)}
                    >
                      {option.label}
                    </FvButton>
                    <p className="text-fv-gray-dark text-sm my-2 mb-0">
                      {enabled
                        ? `${eligibleCount} of ${loads.length} shipments eligible`
                        : option.disabledReason}
                    </p>
                  </ListItem>
                )
              })}
            </List>
          </>
        )}
      </div>

      <div className="bg-fv-blue-50 mt-auto px-6 py-4 border-t border-fv-blue-200 -my-6">
        <FvButton onClick={() => onClose()} icon="times">
          Cancel
        </FvButton>
      </div>
    </div>
  )
}
